import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Check, Lock, RotateCcw, Star, Timer } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ReadingBg } from "@/pages/ReadingHome";
import StoryShelf from "@/components/reading/StoryShelf";
import LibraryBadges from "@/components/reading/LibraryBadges";

// Reading texts attached to a lesson: they open once the lesson is mastered
function TextChips({ texts, open, fr }) {
  if (!texts.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {texts.map((x) => (
        <button key={x.id} onClick={() => open(x)} data-testid={`path-text-${x.id}`}
          className={`rounded-full border-2 border-slate-900 px-3 py-1 text-sm font-bold inline-flex items-center gap-1 transition-colors ${x.unlocked ? (x.passed ? "bg-green-200" : "bg-amber-200 hover:bg-amber-300") : "bg-slate-100 text-slate-400"}`}>
          {x.unlocked ? <BookOpen size={14} /> : <Lock size={14} />} {fr ? "Texte" : "Text"} : {x.title}
        </button>
      ))}
    </div>
  );
}

// Reading Library: texts for children who already read well (no Kiko support), by level
function LibraryShelf({ texts, open, fr, badges, streak }) {
  if (!texts.length) return null;
  const levels = [...new Set(texts.map((x) => x.level))];
  return (
    <section className="mb-10" data-testid="path-library">
      <h2 className="inline-block rounded-full bg-emerald-600 text-white px-5 py-2 font-bold mb-2">{fr ? "Bibliothèque · je lis seul" : "Library · I read on my own"}</h2>
      <p className="text-sm font-semibold text-slate-600 mb-4">{fr ? "Des textes pour les enfants qui savent déjà lire : histoires, fables, documentaires, poèmes." : "Texts for children who can already read: stories, fables, non-fiction, poems."}</p>
      <LibraryBadges badges={badges} streak={streak} lang={fr ? "fr" : "en"} />
      {levels.map((lv) => {
        const xs = texts.filter((x) => x.level === lv);
        return (
          <div key={lv} className="mb-6" data-testid={`path-library-level-${lv}`}>
            <p className="font-bold mb-2">{fr ? "Niveau" : "Level"} {lv} · {xs[0].level_name}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {xs.map((x) => (
                <button key={x.id} onClick={() => open({ ...x, part: "LIB" })} data-testid={`path-lib-${x.id}`}
                  className={`rounded-2xl border-[3px] border-slate-900 bg-white p-2 text-left shadow-[3px_3px_0_0_#0F172A] transition-transform hover:-translate-y-0.5 ${x.unlocked ? "" : "opacity-50"}`}>
                  <img src={x.picture} alt="" className="w-full h-24 object-contain" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                  <p className="font-bold text-sm mt-1 flex items-center gap-1">{x.unlocked ? (x.counted ? <Check size={14} className="text-green-600" /> : x.best ? <Timer size={14} className="text-amber-600" data-testid={`path-lib-unproven-${x.id}`} /> : <BookOpen size={14} />) : <Lock size={14} />} {x.title}</p>
                  <p className="text-xs text-slate-500">{x.genre} · {x.words} {fr ? "mots" : "words"}</p>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}

function TextShelf({ part, name, texts, open, fr }) {
  if (!texts.length) return null;
  return (
    <section className="mb-10" data-testid={`path-shelf-${part}`}>
      <h2 className="inline-block rounded-full bg-slate-900 text-white px-5 py-2 font-bold mb-2">{name}</h2>
      <p className="text-sm font-semibold text-slate-600 mb-4">
        {part === "R" ? (fr ? "Ils s'ouvrent au fil du Niveau 3 (après les leçons 4, 6, 8, 10 et 12)." : "They open along Level 3 (after lessons 4, 6, 8, 10 and 12).")
          : (fr ? "Après tout le Niveau 3, un texte s'ouvre à chaque texte réussi." : "After all of Level 3, each passed text opens the next one.")}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {texts.map((x) => (
          <button key={x.id} onClick={() => open(x)} data-testid={`path-text-${x.id}`}
            className={`rounded-2xl border-[3px] border-slate-900 bg-white p-2 text-left shadow-[3px_3px_0_0_#0F172A] transition-transform hover:-translate-y-0.5 ${x.unlocked ? "" : "opacity-50"}`}>
            <img src={x.picture} alt="" className="w-full h-24 object-contain" loading="lazy" />
            <p className="font-bold text-sm mt-1 flex items-center gap-1">{x.unlocked ? (x.passed ? <Check size={14} className="text-green-600" /> : <BookOpen size={14} />) : <Lock size={14} />} {x.title}</p>
            <p className="text-xs text-slate-500">{x.focus}{part === "ADV" && <span className="ml-1 rounded-full bg-sky-100 text-sky-800 px-2 py-0.5 font-bold" data-testid={`path-text-video-${x.id}`}>▶ {fr ? "Vidéo" : "Video"}</span>}</p>
          </button>
        ))}
      </div>
    </section>
  );
}

export default function ReadingPath() {
  const { childId, lang } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [style, setStyle] = useState("petit");
  const [texts, setTexts] = useState([]);
  const [lib, setLib] = useState([]);
  const [badges, setBadges] = useState(null);
  const [streak, setStreak] = useState(null);
  const [textAcc, setTextAcc] = useState(true);
  useEffect(() => {
    api.get(`/reading/${childId}/${lang}/path`).then(({ data }) => setData(data)).catch(() => navigate(`/kid/${childId}/reading`));
    api.get(`/reading/${childId}/home`).then(({ data }) => setStyle(data.style)).catch(() => {});
    api.get(`/reading/${childId}/${lang}/texts`).then(({ data }) => { setTexts(data.texts); setTextAcc(data.access); }).catch(() => {});
    api.get(`/reading/${childId}/${lang}/library`).then(({ data }) => { setLib(data.texts); setBadges(data.badges); setStreak(data.streak); }).catch(() => {});
  }, [childId, lang, navigate]);
  if (!data) return <ReadingBg petit><div className="p-10 text-center font-bold">…</div></ReadingBg>;
  const petit = style === "petit";
  const fr = lang === "fr";
  const current = data.lessons.find((l) => l.lesson_id === data.current);
  // Level 3 (advanced) belongs to the Grand lecteur kit
  const levels = [...new Set(data.lessons.map((l) => l.level))].filter((lv) => !petit || lv < 3);
  const toGrand = () => api.post(`/reading/${childId}/style`, { style: "grand" }).then(() => setStyle("grand")).catch(() => {});
  const openText = (x) => {
    if (x.unlocked) return navigate(`/kid/${childId}/reading/text/${x.id}`);
    // locked: say what opens it
    const why = x.part === "LIB" ? (fr ? "La bibliothèque est ouverte aux enfants qui ont la matière French, English ou le kit Lecture." : "The library is open to children who have the English or French subject, or the Reading kit.")
      : !textAcc ? (fr ? "Demande à tes parents de débloquer le kit Lecture." : "Ask your parents to unlock the Reading kit.")
      : petit && ["R", "ADV"].includes(x.part) ? (fr ? "Ce texte est dans le Kit Grand lecteur." : "This text is in the Grand lecteur kit.")
      : x.lesson_id ? (fr ? `Réussis la leçon ${x.lesson_id} pour ouvrir ce texte.` : `Pass lesson ${x.lesson_id} to open this text.`)
      : x.part === "R" ? (fr ? "Il s'ouvre au fil du Niveau 3." : "It opens along Level 3.")
      : (fr ? "Il s'ouvre après tout le Niveau 3, texte après texte." : "It opens after all of Level 3, one text after another.");
    toast(why, { icon: "🔒" });
  };
  return (
    <ReadingBg petit={petit}>
      <header className="max-w-3xl mx-auto px-5 pt-5 flex items-center gap-3">
        <button onClick={() => navigate(`/kid/${childId}/reading`)} className="w-11 h-11 rounded-full bg-white border-[3px] border-slate-900 flex items-center justify-center" data-testid="path-back"><ArrowLeft /></button>
        <h1 className="text-2xl sm:text-3xl font-bold">{fr ? "Mon parcours de lecture" : "My reading path"}</h1>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-6">
        <StoryShelf />
        {levels.map((lv) => (
          <section key={lv} className="mb-10">
            <h2 className="inline-block rounded-full bg-slate-900 text-white px-5 py-2 font-bold mb-6" data-testid={`path-level-${lv}`}>
              {petit ? (fr ? `Niveau ${lv}` : `Level ${lv}`) : (fr ? `Compétences de lecture ${lv}` : `Reading skills ${lv}`)}
              {lv === 3 && (fr ? " · Niveau avancé" : " · Advanced")}
            </h2>
            {lv === 3 && (
              <button onClick={() => navigate(`/kid/${childId}/reading/frequent/${lang}`)} className="ml-3 rounded-full bg-yellow-300 border-[3px] border-slate-900 px-4 py-2 font-bold" data-testid="path-frequent-words">
                {fr ? "Les 60 mots fréquents" : "60 frequent words"}
              </button>
            )}
            <div className="flex flex-col gap-5">
              {data.lessons.filter((l) => l.level === lv).map((l, k) => {
                const isCur = current?.lesson_id === l.lesson_id;
                const mastered = l.status === "mastered";
                const color = !l.unlocked ? "bg-slate-200 text-slate-400" : mastered ? "bg-green-400" : isCur ? "bg-yellow-300" : "bg-white";
                return (
                  <motion.div key={l.lesson_id} initial={{ opacity: 0, x: k % 2 ? 40 : -40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: k * 0.05 }}
                    className={`flex items-center gap-4 ${k % 2 ? "sm:ml-24" : "sm:mr-24"}`}>
                    <motion.button whileTap={{ scale: 0.92 }} disabled={!l.unlocked} data-testid={`path-lesson-${l.lesson_id}`}
                      onClick={() => navigate(`/kid/${childId}/reading/lesson/${l.lesson_id}${l.review_due ? "?review=1" : ""}`)}
                      animate={isCur ? { scale: [1, 1.08, 1] } : {}} transition={isCur ? { repeat: Infinity, duration: 1.6 } : {}}
                      className={`relative w-20 h-20 shrink-0 rounded-full border-[3px] border-slate-900 shadow-[4px_4px_0_0_#0F172A] flex items-center justify-center text-2xl font-bold ${color}`}>
                      {!l.unlocked ? <Lock /> : mastered ? <Check size={34} /> : l.lesson_id.split("-")[1]}
                      {l.review_due && <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-orange-500 text-white border-2 border-slate-900 flex items-center justify-center" data-testid={`path-review-${l.lesson_id}`}><RotateCcw size={16} /></span>}
                    </motion.button>
                    <div className="flex-1">
                      <p className={`font-bold text-lg ${!l.unlocked ? "text-slate-400" : ""}`}>{l.title}</p>
                      {mastered && <p className="text-sm font-semibold text-green-700"><Star size={14} className="inline -mt-1 fill-yellow-400 text-yellow-500" /> {fr ? "Maîtrisé" : "Mastered"}</p>}
                      {l.review_due && <p className="text-sm font-semibold text-orange-700">{fr ? "Petite révision !" : "Quick review!"}</p>}
                      <TextChips texts={texts.filter((x) => x.lesson_id === l.lesson_id)} open={openText} fr={fr} />
                    </div>
                    {isCur && <motion.img src="/mascot/kiko.png" alt="Kiko" className="w-16 h-16" animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 1.2 }} data-testid="path-kiko" />}
                  </motion.div>
                );
              })}
            </div>
          </section>
        ))}
        {[["R", fr ? "Textes de révision" : "Revision texts"], ["ADV", fr ? "Textes avancés" : "Advanced texts"]].map(([part, name]) => (
          <TextShelf key={part} part={part} name={name} texts={texts.filter((x) => x.part === part).map((x) => (petit ? { ...x, unlocked: false } : x))} open={openText} fr={fr} />
        ))}
        {!petit && <LibraryShelf texts={lib} open={openText} fr={fr} badges={badges} streak={streak} />}
        {petit && data.lessons.some((l) => l.level === 3) && (
          <section className="rounded-3xl bg-white border-[3px] border-slate-900 shadow-[6px_6px_0_0_#0F172A] p-6 flex flex-wrap items-center gap-4" data-testid="path-level3-grand">
            <img src="/mascot/kiko.png" alt="Kiko" className="w-16 h-16" />
            <div className="flex-1 min-w-[12rem]">
              <p className="font-bold text-lg">{fr ? "Niveau 3 · Niveau avancé" : "Level 3 · Advanced"}</p>
              <p className="text-sm font-semibold text-slate-600">{fr ? "Les sons complexes, avec vidéos et petits textes, sont dans le Kit Grand lecteur." : "Complex sounds, with videos and short texts, are in the Grand lecteur kit."}</p>
            </div>
            <button onClick={toGrand} className="rounded-full bg-blue-600 text-white border-[3px] border-slate-900 px-5 py-2 font-bold" data-testid="path-level3-switch">
              {fr ? "Passer au Grand lecteur" : "Switch to Grand lecteur"}
            </button>
          </section>
        )}
      </main>
    </ReadingBg>
  );
}
